/** @jsx jsx */
import React, { memo, useCallback, type MouseEvent, type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { Flex, xcss } from '@atlaskit/primitives';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { N500, B100 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { OnItemExpandChanged } from '../../../../../common/types/callbacks.tsx';
import type { ItemId } from '../../../../../common/types/item.tsx';

const buttonStyles = css({
	padding: 0,
	margin: 0,
	border: 'none',
	backgroundColor: 'transparent',
	cursor: 'pointer',
	display: 'flex',
	flex: '0 0 auto',
	outline: 'none',
	'&:focus': {
		outline: 'none',
	},
	'&:focus-visible': {
		boxShadow: `inset 0 0 0 2px ${token('color.border.focused', B100)}`,
	},
});

type Props = {
	isExpanded: boolean;
	isRichContentEnabled: boolean;
	id: ItemId;
	label: string;
	ariaControls?: string;
	onExpandChanged: OnItemExpandChanged;
};

const ChevronWrapper = ({ children }: { children: ReactNode }) =>
	isVisualRefreshEnabled() ? (
		<Flex
			testId="roadmap.timeline-table.main.content.list.list-item.expand-button.wrapper"
			justifyContent="center"
			alignItems="center"
			xcss={containerStyles}
		>
			{children}
		</Flex>
	) : (
		children
	);

const ExpandButton = ({
	id,
	label,
	isRichContentEnabled,
	isExpanded,
	ariaControls,
	onExpandChanged,
}: Props) => {
	const onClick = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			// Do not consider clicking this as also clicking on the list item itself
			event.stopPropagation();
			traceUFOPress('timeline-expand-changed', event.timeStamp);
			onExpandChanged(id, isExpanded);
		},
		[id, isExpanded, onExpandChanged],
	);

	const Chevron = isExpanded ? ChevronDownIcon : ChevronRightIcon;
	const chevronElement = (
		<ChevronWrapper>
			<Chevron
				testId={`roadmap.timeline-table.components.list-item.expand-button.container-${id}`}
				color={token('color.icon', N500)}
				label={label}
			/>
		</ChevronWrapper>
	);

	// Performance optimisation - only render interactive button upon proxy interactions
	if (!isRichContentEnabled) {
		return chevronElement;
	}

	return (
		<button
			css={buttonStyles}
			onClick={onClick}
			aria-expanded={isExpanded}
			aria-label={label}
			{...(isExpanded &&
				ariaControls && {
					'aria-controls': ariaControls,
				})}
		>
			{chevronElement}
		</button>
	);
};

export default memo<Props>(ExpandButton);

const containerStyles = xcss({
	width: '24px',
	height: '24px',
});
